import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { UserService } from '../services/user.service';
import * as UserActions from './user.actions';
import {} from './user.selectors';

@Injectable()
export class UserEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _userService = inject(UserService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all users',
        conectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Users
    getAllUsers$ = createEffect(() =>
        this._actions$.pipe(
            ofType(UserActions.loadUsers),
            switchMap(() => {
                return this._userService.getAll().pipe(
                    // Debug
                    // tap((res) => {
                    //     this.logger.log('res', res);
                    // }),
                    map((res) => {
                        return UserActions.loadUsersSuccess({
                            users: res,
                        });
                    }),
                    catchError((error) =>
                        of(
                            UserActions.loadUsersFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Users
    getUser$ = createEffect(() =>
        this._actions$.pipe(
            ofType(UserActions.loadUser),
            switchMap((action) => {
                return this._userService.getOne(action.id).pipe(
                    // Debug
                    // tap((res) => {
                    //     this.logger.log('res', res);
                    // }),
                    map((res) => {
                        return UserActions.loadUserSuccess({
                            user: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            UserActions.loadUserFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Create User
    createUser$ = createEffect(() =>
        this._actions$.pipe(
            ofType(UserActions.createUser),
            switchMap((action) => {
                return this._userService.create(action.userData).pipe(
                    // Debug
                    tap((res) => {
                        this.logger.log('res', res);
                    }),
                    map((res) => {
                        if (!res) {
                            return UserActions.createUserFailure({
                                error: 'No response received from server',
                            });
                        }
                        return UserActions.createUserSuccess({
                            user: res,
                            success: 'User created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            UserActions.createUserFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Update User
    updateUser$ = createEffect(() =>
        this._actions$.pipe(
            ofType(UserActions.updateUser),
            switchMap((action) => {
                return this._userService
                    .update(action.userData, action.id)
                    .pipe(
                        // Debug
                        // tap((res) => {
                        //     this.logger.log('res', res);
                        // }),
                        map((res) => {
                            const user = res[0].return;
                            return UserActions.updateUserSuccess({
                                user: user,
                                success: 'User updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                UserActions.updateUserFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete User
    deleteUser$ = createEffect(() =>
        this._actions$.pipe(
            ofType(UserActions.deleteUser),
            switchMap((action) => {
                return this._userService.delete(action.id).pipe(
                    // Debug
                    // tap((res) => {
                    //     this.logger.log('res', res);
                    // }),
                    map(() => {
                        return UserActions.deleteUserSuccess({
                            id: action.id,
                            success: 'User deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            UserActions.deleteUserFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Toggle User Status
    toggleUserStatus$ = createEffect(() =>
        this._actions$.pipe(
            ofType(UserActions.toggleUserStatus),
            switchMap((action) => {
                return this._userService.toggleStatus(action.userData).pipe(
                    // Debug
                    // tap((res) => {
                    //     this.logger.log('res', res);
                    // }),
                    map((res) => {
                        const user = res[0].return;
                        return UserActions.toggleUserStatusSuccess({
                            user: user,
                            success: 'User status updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            UserActions.toggleUserStatusFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onUserActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    UserActions.createUserSuccess,
                    UserActions.updateUserSuccess,
                    UserActions.deleteUserSuccess,
                    UserActions.toggleUserStatusSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onUserActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    UserActions.loadUsersFailure,
                    UserActions.loadUserFailure,
                    UserActions.createUserFailure,
                    UserActions.updateUserFailure,
                    UserActions.deleteUserFailure,
                    UserActions.toggleUserStatusFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
